// src/hr/format.js
// Display helpers for Time Off + HR Portal. Time is stored in hours (1 day = 8, half day = 4);
// dates are 'YYYY-MM-DD' strings and are formatted without any timezone shift.

const HOURS_PER_DAY = 8;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DASH = ' – '; // spaced en dash

function toDays(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n)) return 0;
  const days = Math.round((n / HOURS_PER_DAY) * 100) / 100;
  return days === 0 ? 0 : days; // never "-0"
}

// 84 -> '10.5', 120 -> '15', 2 -> '0.25'
export function daysNumber(hours) {
  return String(toDays(hours));
}

// 8 -> '1 day', 4 -> '0.5 day', 84 -> '10.5 days', 0 -> '0 days'
export function daysLabel(hours) {
  const days = toDays(hours);
  const singular = days !== 0 && Math.abs(days) <= 1;
  return `${String(days)} ${singular ? 'day' : 'days'}`;
}

function parseDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(date.getTime())) return null;
  return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate(), weekday: date.getUTCDay() };
}

// '2026-11-27' -> 'Fri, Nov 27'
export function dayLabel(date) {
  const d = parseDate(date);
  if (!d) return '';
  return `${WEEKDAYS[d.weekday]}, ${MONTHS[d.month]} ${d.day}`;
}

// ('2026-12-21', '2026-12-23') -> 'Mon, Dec 21 – Wed, Dec 23'; one day -> 'Fri, Nov 27'
export function rangeLabel(start, end) {
  const first = dayLabel(start);
  if (!first) return '';
  if (!end || end === start || !parseDate(end)) return first;
  return `${first}${DASH}${dayLabel(end)}`;
}

// Compact form for timelines: 'Sep 2 – 5', 'Nov 30 – Dec 2', 'Nov 27'
export function shortRangeLabel(start, end) {
  const a = parseDate(start);
  if (!a) return '';
  const head = `${MONTHS[a.month]} ${a.day}`;
  const b = end && end !== start ? parseDate(end) : null;
  if (!b) return head;
  const sameMonth = a.year === b.year && a.month === b.month;
  return `${head}${DASH}${sameMonth ? b.day : `${MONTHS[b.month]} ${b.day}`}`;
}

// 'Jordan Lee' -> 'JL', 'Mary Ann van Dyke' -> 'MD', 'priya' -> 'P', '' -> '?'
export function initials(name) {
  const parts = String(name || '')
    .split(/\s+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}

// Stable color slot for an id: 'c1' ... 'c6'.
export function avatarColor(id) {
  const text = String(id == null ? '' : id);
  let hash = 2166136261; // FNV-1a
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `c${((hash >>> 0) % 6) + 1}`;
}
